import React, { useMemo, useContext } from "react";
import MainStateContext from "../state/mainStateContext";
import { StateContextType as MainStateContextType } from "../types/state.types";
import RCDSStateContext from "./RCDSStateContext";
import RCDSInitialState from "./RCDSInitialState";
import RCDSStateReducer from "./RCDSStateReducer";
import { triggerEvent, wvusLogger } from "../helpers";
import { getRandomChild, getChildByGuid, checkIsChildAvailable } from "./rcdsRequest";

const getLookupErrorMessages = (key: string, childData = {}) => {
  const { givenName = "a different child" }: { givenName?: string } = childData;
  const messages = {
    childNotFound: `We're sorry, but that child is no longer available. Please consider sponsoring ${givenName}.`,
    childIdInvalid: `We're sorry, there was an error looking up that child. Please consider sponsoring ${givenName}.`,
    generalError: "We're sorry, there was an error looking up the child. Please try again."
  };

  return messages[key] || messages["generalError"];
};

const triggerRcdsErrorViewEvent = (errorMessage, errorMessageKey) => {
  // Without this timeout, sometimes the error_view event will be picked up from the dataLayer by GAU but not GA4.
  setTimeout(() => {
    triggerEvent("error_view", {
      error_message: errorMessage,
      error_type: "rcds_error",
      event_label: errorMessageKey,
      checkout_type: "flex_checkout"
    });
  }, 100);
};

const validateBirthdate = (birthdate) => {
  if (typeof birthdate !== "string" || !/^\d{8}$/.test(birthdate)) return false; // Ensure birthdate is a string and 8 digits

  const year = Number(birthdate.substring(0, 4));
  const month = Number(birthdate.substring(4, 6)) - 1; // JS months are 0-based
  const day = Number(birthdate.substring(6, 8));

  // Create date and check if it matches the original input
  const date = new Date(year, month, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day ||
    date > new Date()
  ) {
    return false;
  }

  return true;
};

const getChildDataFromUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const requiredParams = [
    "childid",
    "childimage",
    "childname",
    "countryname",
    "gender",
    "birthdate"
  ];

  // Check if all required parameters are present
  if (!requiredParams.every((param) => urlParams.has(param))) {
    return null;
  }

  // Validate birthdate format and value
  const birthdate = urlParams.get("birthdate");
  if (!validateBirthdate(birthdate)) {
    return null;
  }

  // Format birthdate
  const formattedBirthdate = `${birthdate.substring(0, 4)}-${birthdate.substring(4, 6)}-${birthdate.substring(6, 8)}T00:00:00`;

  // Normalize and validate gender
  const gender = urlParams.get("gender")?.toLowerCase();
  if (gender !== "male" && gender !== "female") {
    return null;
  }

  // Make sure the child image comes from cloudfront.net, if not child image will be blank
  const childImage = urlParams.get("childimage");
  const childImageUrl = childImage.includes("cloudfront.net") ? childImage : "";

  return {
    identifier: urlParams.get("childid"),
    givenName: urlParams.get("childname"),
    gender,
    birthdate: formattedBirthdate,
    age: new Date().getFullYear() - parseInt(birthdate.substring(0, 4)),
    countryName: urlParams.get("countryname"),
    cgpUrl: childImageUrl,
    cgpThumbnailUrl: childImageUrl
  };
};

export function RCDSProvider(props) {
  const { children, initialState = {} } = props;
  const reducerInitialState = { ...RCDSInitialState, ...initialState };
  const [state, dispatch] = React.useReducer(RCDSStateReducer, reducerInitialState);
  const { dispatch: mainDispatch } = useContext(MainStateContext) as MainStateContextType;
  const stateContextMemo = useMemo(() => {
    return {
      state,
      dispatch,
      getChildData: async (id: string) => {
        dispatch({ type: "SET_STATUS", payload: "loading" });
        let childData = state.childData;
        let childDataLookUpStatus: "error" | "success";

        /**********************************************************************************************************
         *
         * Business Logic: Child Data Retrieval with Failover
         *
         * Primary Flow (RCDS):
         * 1. If childid is in querystring, attempt to fetch child data from RCDS
         *    - If child is available: set child data and status to success
         *    - If child not available: search for random child
         *    - If RCDS error occurs: proceed to URL Parameter Failover
         * 2. If no childid in querystring: search for random child
         *    - If error occurs: set status to error
         *
         * URL Parameter Failover:
         * Activates when RCDS fails and all required URL parameters are present:
         * - childid, childimage, childname, countryname, gender, birthdate
         *
         * Business Impact:
         * - Ensures artist events continue uninterrupted during RCDS outages
         * - Maintains child locking in ecomm, with failover lock error override
         *
         *************************************************************************************************************/

        if (id) {
          childData = await getChildByGuid(id)
            .then((data) => {
              if (checkIsChildAvailable(data)) {
                childDataLookUpStatus = "success";
                return data;
              }

              dispatch({ type: "ADD_CHILD_NOT_FOUND", payload: id });

              throw new Error("No child found");
            })
            .catch((error) => {
              if (error?.message?.includes("No child found")) {
                return getRandomChild().then((randomChildData) => {
                  const errorMessageKey = "childIdInvalid";
                  const errorMessage = getLookupErrorMessages(errorMessageKey, randomChildData);
                  triggerRcdsErrorViewEvent(errorMessage, errorMessageKey);
                  childDataLookUpStatus = "success";
                  dispatch({ type: "SET_LOOKUP_ERROR", payload: errorMessage });

                  return randomChildData;
                });
              }

              // RCDS failed, try URL params
              const urlParamsData = getChildDataFromUrlParams();
              if (urlParamsData && urlParamsData.identifier === id) {
                childDataLookUpStatus = "success";
                mainDispatch({ type: "SET_RCDS_FAILOVER", payload: true });
                dispatch({ type: "SET_CHILD_DATA", payload: urlParamsData });
                return urlParamsData;
              }

              return Promise.reject(error);
            })
            .catch((error) => {
              // RCDS failed, try URL params
              const urlParamsData = getChildDataFromUrlParams();
              if (urlParamsData && urlParamsData.identifier === id) {
                childDataLookUpStatus = "success";
                mainDispatch({ type: "SET_RCDS_FAILOVER", payload: true });
                dispatch({ type: "SET_CHILD_DATA", payload: urlParamsData });
                return urlParamsData;
              }

              const errorMessageKey = "generalError";
              const errorMessage = getLookupErrorMessages(errorMessageKey);
              triggerRcdsErrorViewEvent(errorMessage, errorMessageKey);
              wvusLogger.log(
                new Error(
                  `General Error from RCDS: ${errorMessage} on get Child by GUID, the response from RCDS was ${error}`
                ),
                wvusLogger.getLogLevelNames().ERROR
              );
              dispatch({ type: "SET_LOOKUP_ERROR", payload: errorMessage });
              childDataLookUpStatus = "error";
              console.log(error);
              dispatch({ type: "SET_STATUS", payload: "error" });
              return null;
            });
        } else {
          childData = await getRandomChild()
            .then((data) => {
              childDataLookUpStatus = "success";
              return data;
            })
            .catch((error) => {
              const errorMessageKey = "generalError";
              const errorMessage = getLookupErrorMessages(errorMessageKey);
              triggerRcdsErrorViewEvent(errorMessage, errorMessageKey);
              wvusLogger.log(
                new Error(
                  `General Error from RCDS: ${errorMessage} on Get Random Child, the response from RCDS was ${error}`
                ),
                wvusLogger.getLogLevelNames().ERROR
              );
              dispatch({ type: "SET_LOOKUP_ERROR", payload: errorMessage });
              childDataLookUpStatus = "error";
              console.log(error);
              dispatch({ type: "SET_STATUS", payload: "error" });
              return null;
            });
        }

        if (childData) {
          dispatch({ type: "SET_CHILD_DATA", payload: childData });
        }

        dispatch({ type: "SET_STATUS", payload: childDataLookUpStatus });
      },
      setChildDataStatus: (status) => {
        dispatch({ type: "SET_STATUS", payload: status });
      }
    };
  }, [state, dispatch]);

  return <RCDSStateContext.Provider value={stateContextMemo}>{children}</RCDSStateContext.Provider>;
}
